// src/features/system/archiver/api/archiverApi.ts
// ────────────────────────────────────────────────────────────────────────────
// Archiver — FE API layer.
//
// Types + fetch functions for the Parquet archive pipeline dashboard.
// All calls go through /bff/orchestrator/hot-stream/archive/* endpoints.
// ────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

const BASE = '/orchestrator/hot-stream/archive';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PartitionInfo {
  name: string;
  date: string;
  rows: number;
  size_bytes: number;
  size: string;
}

export interface ArchiveTotals {
  summary_rows: number;
  total_archived_rows: number;
  distinct_ns: number;
  total_parquet_files: number;
  total_parquet_bytes: number;
}

export interface PipelineConfig {
  retention_days: number;
}

export interface DBTotals {
  total_partitions: number;
  total_rows: number;
  total_bytes: number;
  total_size: string;
}

export interface SavingsInfo {
  compression_ratio: number;
  saved_bytes: number;
  saved_size: string;
}

export interface ArchiveStatusResponse {
  partitions: PartitionInfo[];
  archive: ArchiveTotals;
  pipeline: PipelineConfig;
  db: DBTotals;
  savings: SavingsInfo;
}

export interface ArchiveSummaryRow {
  scope_ns: string;
  granularity: string;
  period_start: string;
  period_end: string;
  total_rows: number;
  distinct_keys: number;
  distinct_types: number;
  parquet_files: number;
  parquet_bytes: number;
  gcs_prefix?: string;
  oldest_ts?: string;
  newest_ts?: string;
}

export interface ArchiveSummaryResponse {
  summary: ArchiveSummaryRow[];
  totals: {
    total_rows: number;
    total_parquet_bytes: number;
    distinct_ns: number;
  };
  source: string;
}

export interface TriggerResponse {
  status: string;
  message: string;
  error?: string;
}

// ── React Query cache keys ────────────────────────────────────────────────

export const ARCHIVER_KEYS = {
  all: ['archiver'] as const,
  status: () => [...ARCHIVER_KEYS.all, 'status'] as const,
  summary: (granularity: string) => [...ARCHIVER_KEYS.all, 'summary', granularity] as const,
};

// ── Fetch functions ──────────────────────────────────────────────────────

export async function fetchArchiveStatus(): Promise<ArchiveStatusResponse> {
  return bff.get<ArchiveStatusResponse>(`${BASE}/status`);
}

export async function fetchArchiveSummary(
  granularity: 'daily' | 'weekly' | 'monthly' = 'daily',
  limit = 200,
): Promise<ArchiveSummaryResponse> {
  return bff.get<ArchiveSummaryResponse>(
    `${BASE}/summary?granularity=${granularity}&limit=${limit}`,
  );
}

export async function triggerArchiveNow(): Promise<TriggerResponse> {
  return bff.post<TriggerResponse>(`${BASE}/trigger`, {});
}
